import React, { useEffect, useState, useRef } from 'react';

const ZONE_SELECTORS_ALL = [
  '.battle-zone.opponent-front',
  '.battle-zone.opponent-flank',
  '.battle-zone.front',
  '.battle-zone.flank'
];

const ZONE_SELECTORS_OPPONENT_ONLY = [
  '.battle-zone.opponent-front',
  '.battle-zone.opponent-flank',
];

/**
 * Get bounding rects for battle zones (viewport coordinates).
 * @param {'all'|'opponent_only'} zoneScope
 * @returns {Array<{ left: number, top: number, width: number, height: number }>}
 */
function getZoneRects(zoneScope = 'all') {
  const selectors = zoneScope === 'opponent_only' ? ZONE_SELECTORS_OPPONENT_ONLY : ZONE_SELECTORS_ALL;
  const rects = [];
  for (const sel of selectors) {
    const el = document.querySelector(sel);
    if (el) rects.push(el.getBoundingClientRect());
  }
  return rects;
}

/**
 * InfernoNapalmEffect – napalm/fire animation over battle zones.
 * Inferno Drop (319): all 4 zones. Spinosaurus (397): opponent zones only.
 * @param {Object} props
 * @param {Function} props.onComplete - Callback when animation completes
 * @param {string} props.animationKey - Unique key for this instance
 * @param {'all'|'opponent_only'} [props.zoneScope='all']
 */
export const InfernoNapalmEffect = ({ onComplete, animationKey, zoneScope = 'all' }) => {
  const [zoneRects, setZoneRects] = useState([]);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const completedRef = useRef(false);

  useEffect(() => {
    if (!animationKey) return;

    completedRef.current = false;
    const updateRects = () => setZoneRects(getZoneRects(zoneScope));
    // Defer first measure so battle zones are in the DOM
    const t0 = setTimeout(updateRects, 50);

    const DURATION_MS = 2200;

    const intervalId = setInterval(updateRects, 80);

    const finish = () => {
      if (completedRef.current) {
        return;
      }
      completedRef.current = true;
      clearInterval(intervalId);
      if (onCompleteRef.current) {
        onCompleteRef.current();
      }
    };

    const timeoutId = setTimeout(finish, DURATION_MS);

    return () => {
      clearTimeout(t0);
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [animationKey, zoneScope]);

  const useFallback = zoneRects.length === 0;

  return (
    <div
      className="inferno-napalm-effect"
      data-animation-key={animationKey}
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 9998,
      }}
    >
      <style>{`
        @keyframes inferno-napalm-flicker {
          0%, 100% { opacity: 0.85; }
          15% { opacity: 0.95; }
          30% { opacity: 0.8; }
          45% { opacity: 0.9; }
          60% { opacity: 0.82; }
          75% { opacity: 0.92; }
        }
        @keyframes inferno-napalm-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        .inferno-napalm-zone {
          position: fixed;
          border-radius: 8px;
          box-sizing: border-box;
          background: linear-gradient(
            180deg,
            rgba(255, 120, 40, 0.35) 0%,
            rgba(220, 60, 20, 0.5) 40%,
            rgba(180, 30, 10, 0.45) 70%,
            rgba(140, 20, 5, 0.25) 100%
          );
          box-shadow:
            inset 0 0 30px rgba(255, 100, 30, 0.4),
            0 0 20px rgba(255, 80, 20, 0.3);
          animation: inferno-napalm-flicker 0.2s ease-in-out infinite, inferno-napalm-pulse 0.6s ease-in-out infinite;
        }
        .inferno-napalm-fallback {
          position: fixed;
          inset: 0;
          background: linear-gradient(
            180deg,
            rgba(255, 100, 30, 0.4) 0%,
            rgba(200, 50, 15, 0.5) 50%,
            rgba(160, 25, 5, 0.35) 100%
          );
          animation: inferno-napalm-flicker 0.2s ease-in-out infinite;
        }
      `}</style>
      {useFallback ? (
        <div className="inferno-napalm-fallback" />
      ) : (
        zoneRects.map((rect, i) => (
          <div
            key={i}
            className="inferno-napalm-zone"
            style={{
              left: rect.left,
              top: rect.top,
              width: rect.width,
              height: rect.height,
            }}
          />
        ))
      )}
    </div>
  );
};

export default InfernoNapalmEffect;
